const CHANNEL_NAME = "premium_tab_sync";
const ACTIVE_TAB_KEY = "activeTab";
const HEARTBEAT_MS = 2000;
const DEAD_THRESHOLD_MS = 5000;
const PROBE_TIMEOUT_MS = 500;

let guardPromise = null;
let heartbeatInterval = null;
let currentTabId = null;
let channelRef = null;

const generateTabId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;
};

const isEntryStale = (entry) => {
  if (!entry) return true;
  try {
    const parsed = JSON.parse(entry);
    return Date.now() - parsed.ts > DEAD_THRESHOLD_MS;
  } catch {
    return true;
  }
};

const probeOtherTab = (tabId) =>
  new Promise((resolve) => {
    if (typeof BroadcastChannel === "undefined") {
      resolve(false);
      return;
    }

    const channel = new BroadcastChannel(CHANNEL_NAME);
    let settled = false;
    let timeoutId = null;

    const finish = (alive) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      try {
        channel.close();
      } catch (err) {
        // ignore
      }
      resolve(alive);
    };

    channel.onmessage = (event) => {
      const data = event.data || {};
      if (data.type === "heartbeat-response" && data.fromTabId !== tabId) {
        finish(true);
      }
    };

    channel.postMessage({ type: "heartbeat-request", fromTabId: tabId });
    timeoutId = setTimeout(() => finish(false), PROBE_TIMEOUT_MS);
  });

const registerTab = (tabId) => {
  localStorage.setItem(
    ACTIVE_TAB_KEY,
    JSON.stringify({ id: tabId, ts: Date.now() })
  );
};

const startHeartbeat = (tabId) => {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    try {
      registerTab(tabId);
    } catch {
      // ignore
    }
  }, HEARTBEAT_MS);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const startListener = (tabId) => {
  stopListener();
  if (typeof BroadcastChannel === "undefined") return;
  channelRef = new BroadcastChannel(CHANNEL_NAME);
  channelRef.onmessage = (event) => {
    const data = event.data || {};
    if (data.type === "heartbeat-request" && data.fromTabId !== tabId) {
      try {
        channelRef.postMessage({ type: "heartbeat-response", fromTabId: tabId });
      } catch {
        // ignore
      }
    }
  };
};

const stopListener = () => {
  if (channelRef) {
    try {
      channelRef.close();
    } catch {
      // ignore
    }
    channelRef = null;
  }
};

const cleanupTab = () => {
  stopHeartbeat();
  stopListener();
  try {
    const raw = localStorage.getItem(ACTIVE_TAB_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.id === currentTabId) {
        localStorage.removeItem(ACTIVE_TAB_KEY);
      }
    }
  } catch {
    // ignore
  }
};

const runGuard = () =>
  new Promise((resolve) => {
    if (typeof BroadcastChannel === "undefined") {
      resolve(true);
      return;
    }

    const tabId = generateTabId();
    currentTabId = tabId;

    window.addEventListener("beforeunload", cleanupTab);

    try {
      const existing = localStorage.getItem(ACTIVE_TAB_KEY);
      if (!isEntryStale(existing)) {
        const parsed = JSON.parse(existing);
        if (parsed.id !== tabId) {
          probeOtherTab(tabId).then((otherAlive) => {
            if (otherAlive) {
              window.removeEventListener("beforeunload", cleanupTab);
              resolve(false);
            } else {
              registerTab(tabId);
              startHeartbeat(tabId);
              startListener(tabId);
              resolve(true);
            }
          });
          return;
        }
      }

      registerTab(tabId);
      startHeartbeat(tabId);
      startListener(tabId);
      resolve(true);
    } catch {
      resolve(true);
    }
  });

export function enforceSingleTab() {
  if (!guardPromise) {
    guardPromise = runGuard().catch(() => true);
  }
  return guardPromise;
}
